import { useRef, useState, type KeyboardEvent, type TouchEvent } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../../lib/utils";
import { useGrowthFeed, type FeedItem } from "../../../hooks/useGrowthFeed";
import { FeedCard } from "../GrowthFeed";
import ReflectionCard from "../ReflectionCard";
import { Skeleton } from "../../ui/skeleton";

// Reflect (spec 2026-11-04 §2.6): today's reflection drip, the one AI card and
// the daily wisdom — one card at a time, in that order (the feed already puts
// the AI card before wisdom; new accounts get the philosophy and create-space
// cards in their usual order). Each card is the existing GrowthFeed renderer.

/**
 * For now keys a feed card stands for, in Reflect's OWN key space: `step:<takeawayId>`.
 * `skipKeys` takes the same format; a card whose keys intersect it is skipped.
 * Task 8 maps the real For now key onto this format before passing it.
 */
export const relatedKeysOf = (item: FeedItem): string[] =>
  item.type === "companion" ? (item.content.metadata?.relatedActionIds ?? []).map((id) => `step:${id}`) : [];

type Slide = { id: string; kind: "reflection" } | { id: string; kind: "feed"; item: FeedItem };

const SWIPE_PX = 40;
const isTyping = (el: EventTarget | null) =>
  el instanceof HTMLElement && (el.tagName === "TEXTAREA" || el.tagName === "INPUT" || el.isContentEditable);

const ReflectSection = ({ skipKeys = [] }: { skipKeys?: string[] }) => {
  const { t, i18n } = useTranslation();
  const { items, isLoading, reflection } = useGrowthFeed();
  // Tracked by id, so a drip that swaps in another area doesn't shift the view.
  const [currentId, setCurrentId] = useState<string | null>(null);
  const touchX = useRef<number | null>(null);
  const rtl = typeof i18n?.dir === "function" && i18n.dir() === "rtl";

  const slides: Slide[] = [];
  if (reflection) slides.push({ id: `reflection-${reflection.content.spaceId}`, kind: "reflection" });
  for (const item of items) {
    if (relatedKeysOf(item).some((k) => skipKeys.includes(k))) continue;
    slides.push({ id: item.id, kind: "feed", item });
  }

  if (slides.length === 0) {
    return isLoading ? <Skeleton className="h-40 rounded-2xl" /> : null;
  }

  const found = slides.findIndex((s) => s.id === currentId);
  const idx = found >= 0 ? found : 0;
  const current = slides[idx];
  const go = (delta: number) => {
    const next = idx + delta;
    if (next >= 0 && next < slides.length) setCurrentId(slides[next].id);
  };
  const forward = (delta: number) => go(rtl ? -delta : delta);
  const position = t("todayHome.reflect.position", { current: idx + 1, total: slides.length });

  const onKeyDown = (e: KeyboardEvent) => {
    if (isTyping(e.target)) return;
    if (e.key === "ArrowRight") {
      e.preventDefault();
      forward(1);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      forward(-1);
    }
  };
  const onTouchStart = (e: TouchEvent) => {
    touchX.current = e.touches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: TouchEvent) => {
    const start = touchX.current;
    touchX.current = null;
    const end = e.changedTouches[0]?.clientX;
    if (start == null || end == null || Math.abs(end - start) < SWIPE_PX) return;
    forward(end < start ? 1 : -1);
  };

  return (
    <section
      role="region"
      aria-roledescription="carousel"
      aria-labelledby="reflect-title"
      onKeyDown={onKeyDown}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className="space-y-3"
    >
      <div className="flex items-center justify-between gap-3">
        <h2 id="reflect-title" className="text-sm font-semibold text-foreground tracking-tight">
          {t("todayHome.reflect.title")}
        </h2>
        {slides.length > 1 && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => go(-1)}
              disabled={idx === 0}
              aria-label={t("todayHome.reflect.previous")}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground hover:bg-muted disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />
            </button>
            <span aria-hidden className="flex items-center gap-1.5">
              {slides.map((s, i) => (
                <span key={s.id} className={cn("h-1.5 w-1.5 rounded-full", i === idx ? "bg-primary" : "bg-muted-foreground/30")} />
              ))}
            </span>
            <button
              type="button"
              onClick={() => go(1)}
              disabled={idx === slides.length - 1}
              aria-label={t("todayHome.reflect.next")}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground hover:bg-muted disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4 rtl:rotate-180" aria-hidden />
            </button>
          </div>
        )}
      </div>
      <div
        key={current.id}
        role="group"
        aria-roledescription="slide"
        aria-label={position}
        className="motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-200"
      >
        {current.kind === "reflection" && reflection ? (
          <ReflectionCard
            content={reflection.content}
            onAnswer={reflection.answer}
            onSkip={() => {
              reflection.skip();
              const next = slides[idx + 1];
              setCurrentId(next ? next.id : null);
            }}
          />
        ) : current.kind === "feed" ? (
          <FeedCard item={current.item} />
        ) : null}
      </div>
      <p className="sr-only" aria-live="polite">
        {position}
      </p>
    </section>
  );
};

export default ReflectSection;
